<template>
    <div class="room__edit_form">
        <form-text
                :label="$t('ebs.rooms.edit.labels.name')"
                :name="$t('ebs.rooms.edit.name.name')"
                :required="true"
                :v-validate="'required|max:45'"
                :disabled="!roomAllowEdit"
                :data-vv-as="$t('forms.dataVvAs.name')"
                v-model="roomName"
        />
        <form-textarea
                :label="$t('ebs.rooms.edit.labels.description')"
                :name="$t('ebs.rooms.edit.name.description')"
                :v-validate="'max:255'"
                :disabled="!roomAllowEdit"
                v-model="roomDescription"
        />
        <form-image
                :label="$t('ebs.rooms.edit.labels.images')"
                :name="$t('ebs.rooms.edit.name.images')"
                :value="roomImages"
                :multiple="true"
                :disabled="!roomAllowEdit"
                @emit-select="roomUploadImages"
                @emit-remove="roomRemoveImages"
        />
        <form-textarea
                :name="$t('ebs.rooms.edit.labels.location')"
                :label="$t('ebs.rooms.edit.name.location')"
                :size="'medium'"
                :v-validate="'max:255'"
                :disabled="!roomAllowEdit"
                v-model="roomLocation"
        >
            {{ $t('ebs.rooms.edit.text.instructions') }}
        </form-textarea>
        <form-number
                :label="$t('ebs.rooms.edit.labels.capacity')"
                :name="$t('ebs.rooms.edit.name.capacity')"
                :required="true"
                :data-vv-as="$t('forms.dataVvAs.capacity')"
                :disabled="!roomAllowEdit"
                :v-validate="'required|min_value:1'"
                :custom-error-active="true"
                :custom-error-text="$t('ebs.rooms.edit.text.errorText')"
                class="room__edit_form_number"
                v-model="roomCapacity"
        />
        <form-cost
                :label="$t('ebs.rooms.edit.labels.cost')"
                :name="$t('ebs.rooms.edit.name.cost')"
                :items="[
                    { name: $t('ebs.rooms.edit.items.perHour'), value: 'hour' },
                    { name: $t('ebs.rooms.edit.items.perDay'), value: 'day' },
                    { name: $t('ebs.rooms.edit.items.perWeek'), value: 'week' }
                ]"
                class="room__edit_form_cost"
                :value-cost="roomCost"
                :value-cost-period="roomCostPeriod"
                :disabled="!roomAllowEdit"
                @emit-value-cost="roomUpdateCost"
                @emit-value-cost-period="roomUpdateCostPeriod"
        />
        <div class="venue__edit_form_box" v-if="!isSecondStageTokenFromOldLMS">
            <component-title :size="'medium'">{{ $t('ebs.venues.edit.title.sharingTitle') }}</component-title>
            <form-radio-horizontal
                v-for="(workspace, index) in customerWorkspacesList"
                class="room__edit_form_sharing"
                :key="index"
                :label="workspace.name"
                :name="workspace.id"
                :items="[
                    { name: $t('ebs.venues.edit.name.visible'), label: workspace.id, value: true },
                    { name: $t('ebs.venues.edit.name.hidden'), label: workspace.id, value: false }
                ]"
                :disabled="workspace.id === accessedWorkspace || !roomAllowEdit"
                :value="roomForWorkspaces.includes(workspace.id)"
                @input="setRoomSharingWorkspaces(workspace.id, ...arguments)"
            >
            </form-radio-horizontal>
        </div>
        <component-title :size="'medium'">{{ $t('ebs.rooms.edit.title.visibility') }}</component-title>
        <form-checkbox
            class="room__edit_form_status"
            :label="$t('ebs.rooms.edit.labels.status')"
            :name="$t('ebs.rooms.edit.name.status')"
            :switcher="true"
            :activeInactive="true"
            :disabled="!roomAllowEdit"
            :value="roomStatus === 'active'"
            @input="updateRoomStatus"
        >{{ $t('ebs.rooms.edit.text.activeRooms') }}
        </form-checkbox>
        <form-submit
            :disabled="!roomAllowEdit"
            @reset="performReset"
            @submit="performSubmitValidate"
        />
        <pop-notify
            :class="'pop__notify--validation'"
            ref="popnotifyvalidation"
            :title-text="`Something's not quite right`"
            :content-text="'Please check that you have filled out all the fields in the form correctly and try again.'"
            :cancel-button-text="'Ok'"
            :submit-button-active="true"
        />
    </div>
</template>

<script>
import FormText from '@/components/default/forms/FormText'
import FormTextarea from '@/components/default/forms/FormTextarea'
import FormImage from '@/components/default/forms/FormImage'
import ComponentTitle from '@/components/default/shared/ComponentTitle'
import FormRadioHorizontal from '@/components/default/forms/FormRadioHorizontal'
import FormNumber from '@/components/default/forms/FormNumber'
import FormCost from '@/components/default/forms/FormCost'
import FormSubmit from '@/components/default/forms/FormSubmit'
import PopNotify from '@/components/default/shared/PopNotify'
import FormCheckbox from '@/components/default/forms/FormCheckbox'
import { performReset, performSubmitValidate, validateBeforeRouteLeave, watchContentChangeEmit } from '@/helpers/mixins'

export default {
  name: 'room-edit-form',
  mixins: [validateBeforeRouteLeave, performReset, performSubmitValidate, watchContentChangeEmit],
  components: {
    PopNotify,
    FormSubmit,
    FormCost,
    FormNumber,
    FormRadioHorizontal,
    ComponentTitle,
    FormImage,
    FormTextarea,
    FormText,
    FormCheckbox
  },
  props: {
    status: {
      type: String,
      default: 'active'
    }
  },
  computed: {
    isSecondStageTokenFromOldLMS () {
      return this.$store.getters.getAuthUserIsSecondStageTokenOldLMS
    },
    customerWorkspacesList: function () {
      return this.$store.getters.getLmsUserCustomerworkspaces
    },
    customerId: function () {
      return this.$store.getters.getAuthUserSecondStageTokenDataCustomerId
    },
    accessedWorkspace () {
      return this.$store.getters.getAuthUserSecondStageTokenDataWorkspaceId
    },
    roomForWorkspaces: function () {
      return this.$store.getters.getRoomSharingWorkspaces
    },
    roomName: {
      get () {
        return this.$store.getters['getRoomName']
      },
      set (value) {
        this.$store.dispatch('setRoomName', value)
      }
    },
    roomDescription: {
      get () {
        return this.$store.getters['getRoomDescription']
      },
      set (value) {
        this.$store.dispatch('setRoomDescription', value)
      }
    },
    roomImages: {
      get () {
        return this.$store.getters['getRoomImages']
      }
    },
    roomLocation: {
      get () {
        return this.$store.getters['getRoomLocation']
      },
      set (value) {
        this.$store.dispatch('setRoomLocation', value)
      }
    },
    roomCapacity: {
      get () {
        return this.$store.getters['getRoomCapacity']
      },
      set (value) {
        this.$store.dispatch('setRoomCapacity', value)
      }
    },
    roomCost: {
      get () {
        return this.$store.getters['getRoomCost']
      }
    },
    roomCostPeriod: {
      get () {
        return this.$store.getters['getRoomCostPeriod']
      }
    },
    roomStatus: {
      get () {
        return this.$store.getters['getRoomStatus']
      }
    },
    roomAllowEdit () {
      return this.$store.getters.getRoomAllowEdit
    },
    watchContent () {
      return this.roomName + this.roomDescription + this.roomDescription +
      this.roomImages + this.roomLocation + this.roomCapacity +
      this.roomCost + this.roomCostPeriod + this.roomStatus
    },
    //
    rootItemName () {
      return 'room'
    },
    nameActionCreate () {
      return 'createRoom'
    },
    nameActionUpdate () {
      return 'updateRoom'
    },
    nameActionReset () {
      return 'resetRoom'
    }
  },
  methods: {
    getLmsCustomerWorkspaces () {
      return this.$store.dispatch('lmsUserCustomerworkspacesGet', { customerId: this.customerId, status: this.status })
    },
    allowRedirectBeforeRouteLeave: function () {
      this.$eventBus.$emit('room-submit')
    },
    setRoomSharingWorkspaces (id, value) {
      this.$store.dispatch('setRoomSharingWorkspaces', { id, value })
    },
    //
    roomUploadImages: function (data) {
      this.$store.dispatch('setRoomImages', data)
    },
    roomRemoveImages: function (key) {
      this.$store.dispatch('removeRoomImages', key)
    },
    roomUpdateCost: function (value) {
      this.$store.dispatch('setRoomCost', value)
    },
    roomUpdateCostPeriod: function () {
      this.$store.dispatch('setRoomCostPeriod', event.target.value)
    },
    updateRoomStatus (value) {
      let status = value ? 'active' : 'draft'
      this.$store.dispatch('setRoomStatus', status)
    }
  },
  created () {
    if (!this.isSecondStageTokenFromOldLMS) {
      this.getLmsCustomerWorkspaces().then(() => {
        this.setRoomSharingWorkspaces(this.accessedWorkspace, true)
      })
    }
  }
}
</script>
